import { useEffect, useState } from "react";
import { ListPlus, Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { UNIT_OPTIONS, ITEM_TYPE_LABELS } from "@/lib/billingMeta";

export default function OwnerBillingComponentsPage() {
  const [properties, setProperties] = useState([]);
  const [propId, setPropId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/properties").then((r) => {
      const ps = r.data || [];
      setProperties(ps);
      if (ps[0]) setPropId(ps[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!propId) return;
    setLoading(true);
    apiClient.get(`/properties/${propId}/billing-defaults`)
      .then((r) => setItems((r.data.extra_defaults || []).map((x) => ({ ...x }))))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [propId]);

  const add = () => setItems((c) => [...c, { name: "", type: "fixed", unit: "", price_per_unit: "", amount: 0, note: "" }]);
  const rm = (i) => setItems((c) => c.filter((_, idx) => idx !== i));
  const set = (i, k, v) => setItems((c) => c.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const save = async () => {
    for (const it of items) { if (!String(it.name).trim()) { toast.error("Nama komponen wajib diisi."); return; } }
    setSaving(true);
    try {
      await apiClient.put(`/properties/${propId}/billing-defaults`, {
        extra_defaults: items.map((x) => ({
          name: String(x.name).trim(),
          type: x.type || "fixed",
          unit: (x.type && x.type !== "fixed") ? (x.unit || null) : null,
          price_per_unit: (x.type && x.type !== "fixed") ? Number(x.price_per_unit || 0) : null,
          amount: (!x.type || x.type === "fixed") ? Number(x.amount || 0) : 0,
          note: x.note || null,
        })),
      });
      toast.success("Template komponen biaya disimpan.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <OwnerLayout>
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><ListPlus size={22} className="text-primary" /> Komponen Biaya</h1>
        <p className="text-sm text-muted-foreground">Template biaya tambahan (PAM, Pemeliharaan, dll) yang otomatis dipakai saat Generate Billing.</p>
      </div>

      <div className="max-w-2xl space-y-5">
        {properties.length === 0 ? (
          <div className="bg-card border border-border rounded-2xl p-6 text-center text-muted-foreground" data-testid="components-no-property">
            Belum ada properti, atau gagal memuat daftar properti. Tambahkan properti terlebih dahulu.
          </div>
        ) : (
        <>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Properti</label>
          <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="components-property"
            className="w-full rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary">
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>
        ) : (
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="components-editor">
            {items.length === 0 ? <p className="text-sm text-muted-foreground mb-3">Belum ada komponen default.</p> :
              <div className="space-y-2 mb-3">
                {items.map((it, i) => {
                  const type = it.type || "fixed";
                  return (
                  <div key={i} className="rounded-xl border border-border p-3 space-y-2" data-testid={`component-row-${i}`}>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input value={it.name} onChange={(e) => set(i, "name", e.target.value)} placeholder="Nama (mis. Air PAM)" data-testid={`component-name-${i}`}
                        className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                      <select value={type} onChange={(e) => set(i, "type", e.target.value)} data-testid={`component-type-${i}`}
                        className="sm:w-52 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                        {Object.entries(ITEM_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <button onClick={() => rm(i)} data-testid={`component-remove-${i}`} className="grid place-items-center w-10 h-10 shrink-0 rounded-xl border border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={16} /></button>
                    </div>
                    {type === "fixed" ? (
                      <input type="number" value={it.amount} onChange={(e) => set(i, "amount", e.target.value)} placeholder="Nominal (Rp)" data-testid={`component-amount-${i}`}
                        className="w-full sm:w-1/2 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                    ) : (
                      <div className="flex gap-2">
                        <input value={it.unit || ""} onChange={(e) => set(i, "unit", e.target.value)} list="unit-options" placeholder="Satuan (mis. m³)" data-testid={`component-unit-${i}`}
                          className="w-1/3 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                        <input type="number" value={it.price_per_unit} onChange={(e) => set(i, "price_per_unit", e.target.value)} placeholder="Harga / satuan" data-testid={`component-price-${i}`}
                          className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    )}
                    <input value={it.note || ""} onChange={(e) => set(i, "note", e.target.value)} placeholder="Catatan (opsional)"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  );
                })}
              </div>}
            <datalist id="unit-options">{UNIT_OPTIONS.map((u) => <option key={u} value={u} />)}</datalist>
            <button onClick={add} data-testid="component-add" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold hover:bg-secondary/60"><Plus size={15} /> Tambah Komponen</button>
          </div>
        )}

        <button onClick={save} disabled={saving || !propId} data-testid="components-save"
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Simpan Template
        </button>
        </>
        )}
      </div>
    </OwnerLayout>
  );
}
